import { call } from '@advanza/api'
import { Button } from '@advanza/ui'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import TextAreaField from '@trustoo/components/input/TextAreaField'
import BaseModal, {
    BaseModalFooter,
    BaseModalHeader,
    BaseModalMainContent,
    ScrollArea,
} from '@trustoo/components/modal/BaseModal'
import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { MIcon } from '../components/shared'
import { VerificationStatus, VerificationSubject } from './subjects'
import { VerificationRow } from './VerificationContext'

interface HistoryEntry {
    design_system_verification_comment_id: number
    status: VerificationStatus
    comment: string | null
    source_hash: string
    created: string | null
    user_name: string | null
}

interface Props {
    subject: VerificationSubject
    currentRow?: VerificationRow
    canWrite: boolean
    onSaved?: (row: VerificationRow) => void
}

const STATUS_OPTIONS: { value: VerificationStatus; label: string }[] = [
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'changes_requested', label: 'Changes requested' },
]

const STATUS_LABELS: Record<VerificationStatus, string> = {
    approved: 'Approved',
    changes_requested: 'Changes requested',
    pending: 'Pending',
}

const STATUS_COLORS: Record<VerificationStatus, { selected: string; idle: string; label: string }> =
    {
        pending: {
            selected: 'border-amber-500 bg-amber-100 font-medium text-amber-800',
            idle: 'border-amber-200 bg-base-white text-amber-700 hover:border-amber-400',
            label: 'text-amber-700',
        },
        approved: {
            selected: 'border-green-500 bg-green-100 font-medium text-green-800',
            idle: 'border-green-200 bg-base-white text-green-700 hover:border-green-400',
            label: 'text-green-700',
        },
        changes_requested: {
            selected: 'border-red-500 bg-red-100 font-medium text-red-800',
            idle: 'border-red-200 bg-base-white text-red-700 hover:border-red-400',
            label: 'text-red-700',
        },
    }

interface SaveResponse {
    item: VerificationRow
    comments: HistoryEntry[]
}

/**
 * Sign-off dialog for one design-system subject. Status and comments save
 * independently so a designer can approve without writing a note, or leave a
 * note without changing the status.
 */
const DesignSystemVerificationModal = NiceModal.create(
    ({ subject, currentRow, canWrite, onSaved }: Props) => {
        const modal = useModal()
        const [row, setRow] = useState<VerificationRow | undefined>(currentRow)
        const [status, setStatus] = useState<VerificationStatus>(currentRow?.status ?? 'pending')
        const [comment, setComment] = useState('')
        const [history, setHistory] = useState<HistoryEntry[]>([])
        const [historyOpen, setHistoryOpen] = useState(false)
        const [loading, setLoading] = useState(true)
        const [savingStatus, setSavingStatus] = useState(false)
        const [savingComment, setSavingComment] = useState(false)
        const [error, setError] = useState<string | null>(null)

        useEffect(() => {
            const query = new URLSearchParams({
                subject_type: subject.type,
                subject_key: subject.key,
                version: subject.version,
            })

            let cancelled = false

            call(`office/design-system-verifications/get-comments?${query.toString()}`)
                .then((response: { comments: HistoryEntry[] }) => {
                    if (!cancelled) {
                        setHistory(response.comments)
                        setLoading(false)
                    }
                })
                .catch(() => {
                    if (!cancelled) {
                        setError('Could not load the history')
                        setLoading(false)
                    }
                })

            return () => {
                cancelled = true
            }
        }, [subject.type, subject.key, subject.version])

        const isStale = row?.status === 'approved' && row.source_hash !== subject.hash
        const subjectPayload = {
            subject_type: subject.type,
            subject_key: subject.key,
            version: subject.version,
            source_hash: subject.hash,
        }

        const applyResponse = (response: SaveResponse) => {
            setRow(response.item)
            setHistory(response.comments)
            onSaved?.(response.item)
        }

        const onClose = () => {
            modal.resolve(row ?? null)
            modal.hide()
        }

        const onSaveStatus = async () => {
            setSavingStatus(true)
            setError(null)

            try {
                const response = (await call('office/design-system-verifications/save', {
                    method: 'POST',
                    json: { ...subjectPayload, status },
                })) as SaveResponse
                applyResponse(response)
                modal.resolve(response.item)
                modal.hide()
            } catch {
                setError('Could not save the status')
                setSavingStatus(false)
            }
        }

        const onSaveComment = async () => {
            const trimmed = comment.trim()

            if (trimmed.length === 0) {
                return
            }

            setSavingComment(true)
            setError(null)

            try {
                const response = (await call('office/design-system-verifications/add-comment', {
                    method: 'POST',
                    json: { ...subjectPayload, comment: trimmed },
                })) as SaveResponse
                applyResponse(response)
                setComment('')
            } catch {
                setError('Could not save the comment')
            } finally {
                setSavingComment(false)
            }
        }

        return (
            <BaseModal onClose={onClose} maxWidth="520px" scrollArea={ScrollArea.CONTENT}>
                <BaseModalHeader>
                    <h2 className="text-xl font-bold">Verify {subject.label}</h2>
                    <div className="mt-spacing-xs font-mono text-xs text-text-tertiary">
                        {subject.type} · {subject.key} · {subject.version}
                    </div>
                </BaseModalHeader>
                <BaseModalMainContent>
                    <div className="space-y-spacing-xl">
                        {isStale && (
                            <div className="flex items-start gap-spacing-md rounded-radius-sm border border-amber-200 bg-amber-50 p-spacing-lg text-sm text-amber-800">
                                <MIcon name="update" className="mt-spacing-xxs" />
                                <div>
                                    Approved against a different source. Re-approve to confirm the
                                    current version.
                                </div>
                            </div>
                        )}

                        <div className="space-y-spacing-sm">
                            <div className="text-sm font-semibold text-text-primary">Status</div>
                            <div className="flex flex-wrap gap-spacing-md">
                                {STATUS_OPTIONS.map((option) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        disabled={!canWrite}
                                        onClick={() => setStatus(option.value)}
                                        className={clsx(
                                            'rounded-radius-sm border px-spacing-lg py-spacing-sm text-sm',
                                            status === option.value
                                                ? STATUS_COLORS[option.value].selected
                                                : STATUS_COLORS[option.value].idle,
                                            !canWrite && 'cursor-not-allowed opacity-60',
                                        )}>
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-spacing-md">
                            <TextAreaField
                                legend="Comment"
                                value={comment}
                                disabled={!canWrite || savingComment}
                                placeholder="What is right or wrong about this?"
                                textAreaClassName="min-h-[88px] bg-base-white text-xs"
                                onChange={(event) => setComment(event.target.value)}
                            />
                            {canWrite && (
                                <div className="flex justify-end">
                                    <Button
                                        name="secondary"
                                        onClick={onSaveComment}
                                        disabled={savingComment || comment.trim().length === 0}>
                                        {savingComment ? 'Adding…' : 'Add comment'}
                                    </Button>
                                </div>
                            )}
                        </div>

                        <div>
                            <button
                                type="button"
                                onClick={() => setHistoryOpen((open) => !open)}
                                className="flex w-full items-center justify-between rounded-radius-sm border border-neutral-700 bg-neutral-700 px-spacing-lg py-spacing-md text-left text-sm font-semibold text-base-white hover:bg-neutral-800">
                                <span>
                                    History
                                    {!loading && history.length > 0 ? ` (${history.length})` : ''}
                                </span>
                                <MIcon
                                    name={historyOpen ? 'expand_less' : 'expand_more'}
                                    className="text-neutral-200"
                                />
                            </button>
                            {historyOpen && (
                                <div className="mt-spacing-md space-y-spacing-md">
                                    {loading && (
                                        <div className="text-sm text-text-tertiary">Loading…</div>
                                    )}
                                    {!loading && history.length === 0 && (
                                        <div className="text-sm text-text-tertiary">
                                            Nobody has reviewed this yet.
                                        </div>
                                    )}
                                    {history.map((entry) => (
                                        <div
                                            key={entry.design_system_verification_comment_id}
                                            className="rounded-radius-sm border border-border-secondary bg-base-white p-spacing-lg">
                                            <div className="flex flex-wrap items-center gap-spacing-md text-xs text-text-tertiary">
                                                <span
                                                    className={clsx(
                                                        'font-semibold',
                                                        STATUS_COLORS[entry.status].label,
                                                    )}>
                                                    {STATUS_LABELS[entry.status]}
                                                </span>
                                                <span>by {entry.user_name ?? 'unknown'}</span>
                                                {entry.created && <span>· {entry.created}</span>}
                                            </div>
                                            {entry.comment && (
                                                <div className="mt-spacing-xs whitespace-pre-wrap text-sm text-text-primary">
                                                    {entry.comment}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {error && <div className="text-sm text-red-700">{error}</div>}
                    </div>
                </BaseModalMainContent>
                <BaseModalFooter className="flex items-center justify-end gap-spacing-lg">
                    <Button name="secondary" onClick={onClose}>
                        Close
                    </Button>
                    {canWrite && (
                        <Button onClick={onSaveStatus} disabled={savingStatus}>
                            {savingStatus ? 'Saving…' : 'Save status'}
                        </Button>
                    )}
                </BaseModalFooter>
            </BaseModal>
        )
    },
)

export default DesignSystemVerificationModal
